import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "@/app/rootReducer";
import {
  fetchTeamOverview,
  fetchTeamCourseProgress,
  fetchAvailablePaths,
  assignPathToTeam,
} from "@/api/managerTeamView/managerTeamViewApi";
import type {
  TeamOverviewResponse,
  TeamCourseProgressResponse,
  TeamMember,
  AvailablePath,
  AssignPathPayload,
  AssignPathResponse,
} from "@/api/managerTeamView/types";

export interface ManagerTeamViewState {
  overview: TeamOverviewResponse | null;
  courseProgress: TeamCourseProgressResponse | null;
  availablePaths: AvailablePath[];
  loading: boolean;
  courseProgressLoading: boolean;
  pathsLoading: boolean;
  assignLoading: boolean;
  error: string | null;
  courseProgressError: string | null;
  selectedMemberId: number | null;
  searchTerm: string;
}

const initialState: ManagerTeamViewState = {
  overview: null,
  courseProgress: null,
  availablePaths: [],
  loading: false,
  courseProgressLoading: false,
  pathsLoading: false,
  assignLoading: false,
  error: null,
  courseProgressError: null,
  selectedMemberId: null,
  searchTerm: "",
};

export const fetchTeamOverviewThunk = createAsyncThunk<
  TeamOverviewResponse,
  void,
  { rejectValue: string; state: RootState }
>("managerTeamView/fetchTeamOverview", async (_, { rejectWithValue }) => {
  try {
    return await fetchTeamOverview();
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.error ||
        error.message ||
        "Failed to fetch team overview",
    );
  }
});

export const fetchTeamCourseProgressThunk = createAsyncThunk<
  TeamCourseProgressResponse,
  void,
  { rejectValue: string; state: RootState }
>("managerTeamView/fetchTeamCourseProgress", async (_, { rejectWithValue }) => {
  try {
    return await fetchTeamCourseProgress();
  } catch (error) {
    const err = error as {
      response?: { data?: { error?: string } };
      message?: string;
    };
    return rejectWithValue(
      err.response?.data?.error ||
        err.message ||
        "Failed to fetch team course progress",
    );
  }
});

export const fetchAvailablePathsThunk = createAsyncThunk<
  AvailablePath[],
  void,
  { rejectValue: string }
>("managerTeamView/fetchAvailablePaths", async (_, { rejectWithValue }) => {
  try {
    return await fetchAvailablePaths();
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.error ||
        error.message ||
        "Failed to fetch available paths",
    );
  }
});

export const assignPathToTeamThunk = createAsyncThunk<
  AssignPathResponse,
  AssignPathPayload,
  { rejectValue: string }
>("managerTeamView/assignPathToTeam", async (payload, { rejectWithValue }) => {
  try {
    return await assignPathToTeam(payload);
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.error || error.message || "Failed to assign path",
    );
  }
});

const managerTeamViewSlice = createSlice({
  name: "managerTeamView",
  initialState,
  reducers: {
    setSelectedMemberId: (state, action: PayloadAction<number | null>) => {
      state.selectedMemberId = action.payload;
    },
    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.searchTerm = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch team overview
      .addCase(fetchTeamOverviewThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTeamOverviewThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.overview = action.payload;
      })
      .addCase(fetchTeamOverviewThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch team overview";
      })
      // Fetch team course progress
      .addCase(fetchTeamCourseProgressThunk.pending, (state) => {
        state.courseProgressLoading = true;
        state.courseProgressError = null;
      })
      .addCase(fetchTeamCourseProgressThunk.fulfilled, (state, action) => {
        state.courseProgressLoading = false;
        state.courseProgress = action.payload;
      })
      .addCase(fetchTeamCourseProgressThunk.rejected, (state, action) => {
        state.courseProgressLoading = false;
        state.courseProgressError =
          action.payload || "Failed to fetch team course progress";
      })
      // Fetch available paths
      .addCase(fetchAvailablePathsThunk.pending, (state) => {
        state.pathsLoading = true;
      })
      .addCase(fetchAvailablePathsThunk.fulfilled, (state, action) => {
        state.pathsLoading = false;
        state.availablePaths = action.payload;
      })
      .addCase(fetchAvailablePathsThunk.rejected, (state, action) => {
        state.pathsLoading = false;
      })
      // Assign path
      .addCase(assignPathToTeamThunk.pending, (state) => {
        state.assignLoading = true;
        state.error = null;
      })
      .addCase(assignPathToTeamThunk.fulfilled, (state) => {
        state.assignLoading = false;
      })
      .addCase(assignPathToTeamThunk.rejected, (state, action) => {
        state.assignLoading = false;
        state.error = action.payload || "Failed to assign path";
      });
  },
});

export const { setSelectedMemberId, setSearchTerm, clearError } =
  managerTeamViewSlice.actions;

// Selectors
export const selectTeamOverview = (state: RootState) =>
  state.managerTeamView?.overview;
export const selectTeamMembers = (state: RootState) =>
  state.managerTeamView?.overview?.teamMembers || [];
export const selectTeamCourseProgress = (state: RootState) =>
  state.managerTeamView?.courseProgress;
export const selectTeamCourseMembers = (state: RootState) =>
  state.managerTeamView?.courseProgress?.teamMembers || [];
export const selectCourseProgressLoading = (state: RootState) =>
  state.managerTeamView?.courseProgressLoading || false;
export const selectCourseProgressError = (state: RootState) =>
  state.managerTeamView?.courseProgressError;
export const selectAvailablePaths = (state: RootState) =>
  state.managerTeamView?.availablePaths || [];
export const selectLoading = (state: RootState) =>
  state.managerTeamView?.loading || false;
export const selectAssignLoading = (state: RootState) =>
  state.managerTeamView?.assignLoading || false;
export const selectError = (state: RootState) => state.managerTeamView?.error;
export const selectSelectedMemberId = (state: RootState) =>
  state.managerTeamView?.selectedMemberId;
export const selectSearchTerm = (state: RootState) =>
  state.managerTeamView?.searchTerm || "";

export default managerTeamViewSlice.reducer;
